import moment from "moment";
import { IconBullet } from "../Icons";
import { ExpandableText } from "./ExpandableText";
import { useTranslation } from "react-i18next";
import {
  getTimelineItemDepartamentoId,
  parseTimelineSnapshot,
} from "@/utils/timelineDepartamento";

type TimelineTaskSnapshot = {
  id_departamento?: number | string | null;
  status?: number | string;
  date?: string | Date;
  title?: string;
  description?: string;
  [key: string]: unknown;
};

export function TimelineTask({ data, departamentos }: any) {
  const { t } = useTranslation();
  const parsedNew = parseTimelineSnapshot<TimelineTaskSnapshot>(data?.new);
  const parsedOld = parseTimelineSnapshot<TimelineTaskSnapshot>(data?.old);
  const departamentoId = getTimelineItemDepartamentoId(data);
  // Função auxiliar para normalizar valores para comparação
  function normalizeValue(value: any): string {
    if (value === null || value === undefined || value === "null") return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }

  // Função auxiliar para verificar se dois valores são realmente diferentes
  function areValuesDifferent(oldVal: any, newVal: any): boolean {
    const normalizedOld = normalizeValue(oldVal);
    const normalizedNew = normalizeValue(newVal);
    return normalizedOld !== normalizedNew;
  }

  function encontrarMudancas(objeto: any) {
    if (!objeto.old || !objeto.new) return {};

    const oldObj = parseTimelineSnapshot<TimelineTaskSnapshot>(objeto.old);
    const newObj = parseTimelineSnapshot<TimelineTaskSnapshot>(objeto.new);
    if (!oldObj || !newObj) return {};

    const mudancas: any = {};

    const nomePropriedades: any = {
      date_end: "Data Final",
      date_init: "Data Inicial",
      id_responsavel: "Responsável",
      status: "Status",
      title: "Título",
      date: "Data",
      description: "Descrição",
      id_departamento: "Departamento",
    };

    // Criar cópias dos objetos para não modificar os originais
    const oldCopy = { ...oldObj };
    const newCopy = { ...newObj };

    Object.keys(newCopy).forEach((chave) => {
      const nomeEmPortugues = nomePropriedades[chave] || chave;

      // Processar datas
      if (chave === "date_end" || chave === "date_init" || chave === "date") {
        if (oldCopy[chave]) {
          oldCopy[chave] = moment.utc(oldCopy[chave]).format("DD/MM/YYYY");
        }
        if (newCopy[chave]) {
          newCopy[chave] = moment.utc(newCopy[chave]).format("DD/MM/YYYY");
        }
      }

      // Processar departamento
      if (chave === "id_departamento") {
        if (oldCopy[chave]) {
          oldCopy[chave] = getNomeDepartamentoById(oldCopy[chave]);
        }
        if (newCopy[chave]) {
          newCopy[chave] = getNomeDepartamentoById(newCopy[chave]);
        }
      }

      // Verificar se realmente houve mudança
      if (areValuesDifferent(oldCopy[chave], newCopy[chave])) {
        mudancas[nomeEmPortugues] = {
          antigo: oldCopy[chave] || "Vazio",
          novo: newCopy[chave] || "Vazio",
          Status_Desk: "Diferentes",
        };
      }
    });
    return mudancas;
  }

  function getNomeDepartamentoById(id?: number | string | null) {
    const item = departamentos.find((element: any) => element.id == id);
    return item ? item.nome : t("common:general");
  }
  /* const renderTextWithLinks = (text: any) => {
    if (typeof text !== "string") {
      console.error("Provided text is not a string:", text);
      return null; // Or handle the error appropriately
    }

    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return text.split("\n").map((line, index) => (
      <div key={index}>
        {line.split(urlRegex).map((part, i) =>
          urlRegex.test(part) ? (
            <a
              key={i}
              href={part}
              className="text-blue-500 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {part}
            </a>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </div>
    ));
  }; */

  return (
    <>
      <div className="hidden sm:flex flex-col gap-1 items-center">
        <div className="w-[2px] h-full bg-black rounded-lg" />
        <IconBullet />
        <div className="w-[2px] h-full bg-black rounded-lg" />
      </div>
      <div className="bg-cinza-100 rounded-3xl p-4 flex flex-col gap-1 text-sm mb-4 w-full">
        <div className="flex justify-between text-base">
          <b>
            {data.alteracao === "created" && t("userGoOn:timeline.task_created")}
            {data.alteracao === "update" && t("userGoOn:timeline.task_edited")}
            {data.alteracao === "delete" && t("userGoOn:timeline.task_deleted")}
            {data.alteracao === "change status" &&
              (parsedNew?.status == 2
                ? t("userGoOn:timeline.task_finished")
                : t("userGoOn:timeline.task_opened"))}
          </b>
          <span className="bg-white px-3 py-px text-azul-400 rounded-2xl text-sm">
            {getNomeDepartamentoById(departamentoId)}
          </span>
        </div>

        <label className="flex items-center gap-1">
          <span>{t("userGoOn:timeline.responsible")}</span>
          <div className="flex gap-2 items-center">
            <div className="flex items-center gap-1">
              {data?.tarefa?.responsavel?.url_img?.path && (
                <img
                  src={data?.tarefa?.responsavel?.url_img?.path}
                  alt={data.tarefa?.responsavel?.name}
                  className="w-5 h-5 rounded-full"
                />
              )}
              <b>
                {data.tarefa?.responsavel?.name}{" "}
                {data.tarefa?.responsavel?.last_name}
              </b>
            </div>
          </div>
        </label>
        <label className="flex items-center gap-1">
          <span>{t("userGoOn:timeline.date")}</span>
          <div className="flex gap-2 items-center">
            <b>
              {parsedNew?.date &&
                moment(parsedNew.date).format("DD/MM/YYYY - HH:mm")}
            </b>
          </div>
        </label>

        {data.alteracao === "update" && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4">
              {Object.keys(encontrarMudancas(data)).map((key) => (
                <p key={key} className="mb-2">
                  <span>
                    {t("userGoOn:timeline.changed")} <b>{key}</b> {t("userGoOn:timeline.from")}{" "}
                  </span>
                  <span>
                    {typeof encontrarMudancas(data)[key].antigo === "object" ? (
                      <span> [Objeto]</span>
                    ) : (
                      <span>
                        <ExpandableText
                          content={
                            encontrarMudancas(data)[key].antigo ||
                            `<p>${t("userGoOn:timeline.no_information")}</p>`
                          }
                          maxLength={100}
                        />
                      </span>
                    )}
                    {" " + t("userGoOn:timeline.to") + " "}
                    {typeof encontrarMudancas(data)[key].novo === "object" ? (
                      <span>[{t("userGoOn:timeline.object")}]</span>
                    ) : (
                      <span>
                        <ExpandableText
                          content={
                            encontrarMudancas(data)[key].novo ||
                            `<p>${t("userGoOn:timeline.no_information")}</p>`
                          }
                          maxLength={100}
                        />
                      </span>
                    )}
                  </span>
                </p>
              ))}
            </div>
          </>
        )}
        {data.alteracao === "created" && data.new && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4">
              <p>
                {t("userGoOn:timeline.title")}: <br />{" "}
                <b>{parsedNew?.title}</b>
              </p>
              <p>
                {t("userGoOn:timeline.description")}: <br />{" "}
                <b>
                  <ExpandableText
                    content={
                      parsedNew?.description ||
                      `<p>${t("userGoOn:timeline.no_information")}</p>`
                    }
                    maxLength={150}
                  />
                </b>
              </p>
            </div>
          </>
        )}
        {data.alteracao === "delete" && data.tarefa && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4">
              <p>
                {t("userGoOn:timeline.title")}: <br /> <b>{data.tarefa.title}</b>
              </p>
              <p>
                {t("userGoOn:timeline.description")}: <br />{" "}
                <b>
                  <ExpandableText
                    content={
                      data.tarefa.description || `<p>${t("userGoOn:timeline.no_information")}</p>`
                    }
                    maxLength={150}
                  />
                </b>
              </p>
            </div>
          </>
        )}
        {data.alteracao === "change status" && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4">
              <p>
                {t("userGoOn:timeline.title")}: <br /> {parsedOld?.title}
              </p>
              <p>
                {t("userGoOn:timeline.description")}: <br />{" "}
                <ExpandableText
                  content={
                    parsedOld?.description || `<p>${t("userGoOn:timeline.no_information")}</p>`
                  }
                  maxLength={150}
                />
              </p>
            </div>
          </>
        )}

        <span className="text-xs text-cinza-800 flex flex-wrap items-center gap-2">
          {t("userGoOn:timeline.created_at")} {moment(data.createdAt).format("DD/MM/YYYY - HH:mm")} {t("userGoOn:timeline.by")}:
          <div className="flex items-center gap-1">
            {data.user.url_img && (
              <img
                src={data.user.url_img}
                alt={data.user.name}
                className="w-5 h-5 rounded-full"
              />
            )}
            {data.user.name} {data.user.last_name}
          </div>
        </span>
      </div>
    </>
  );
}
